import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { Icon } from './Icon.jsx';

// Case-insensitive substring filter for the searchable popover. Matches
// against the option's label OR its description (so typing "RFC" surfaces
// "Tech design" via its blurb). Original option order is preserved — the
// dictionaries this powers are tiny and fixed, so ranked/fuzzy matching is
// out of scope (cf. ClientSelect's filterClients, which prefix-ranks names).
// Exported so the behaviour can be pinned down in unit tests without a DOM.
export function filterOptions(options, query, getLabel, getDescription) {
  const q = String(query || '').trim().toLowerCase();
  const arr = (options || []).filter((o) => o != null);
  if (!q) return arr;
  return arr.filter((o) => {
    const label = String((getLabel ? getLabel(o) : o) ?? '').toLowerCase();
    if (label.includes(q)) return true;
    const desc = getDescription ? String(getDescription(o) ?? '').toLowerCase() : '';
    return !!desc && desc.includes(q);
  });
}

export function Select({
  value,
  options,
  placeholder,
  onChange,
  error,
  disabled,
  getKey,
  getLabel,
  getMeta,
  getDescription,
  emptyLabel,
  // Opt-in search-filterable mode, modelled on ClientSelect. Off by default
  // so every other Select in the app renders exactly as before.
  searchable = false,
  searchPlaceholder = 'Search…',
  // Singular noun for the no-match dead-end ("No <noun> matches …"). When
  // absent we fall back to a generic phrasing so the component stays domain-free.
  searchNoun = '',
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlight, setHighlight] = useState(0);
  const ref = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  // Stable id prefix per instance so the combobox/listbox wiring works even
  // when multiple searchable Selects coexist (import overlays have one per row).
  const listboxId = useId();
  const optionId = (i) => `${listboxId}-opt-${i}`;

  // Search row always renders when `searchable` is on — no item-count
  // threshold. The activity dictionaries are short (3–8 items); the row is
  // there for typeahead speed and parity with the Client picker.
  const showSearch = searchable;

  useEffect(() => {
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const filtered = useMemo(() => {
    if (!showSearch) return options;
    return filterOptions(options, query, getLabel, getDescription);
  }, [options, query, showSearch, getLabel, getDescription]);

  // Each time the popover opens: clear the query and focus the search input.
  // Closing discards the query (it's reset on the next open).
  useEffect(() => {
    if (!open || !showSearch) return;
    setQuery('');
    setHighlight(0);
    const t = setTimeout(() => inputRef.current?.focus(), 0);
    return () => clearTimeout(t);
  }, [open, showSearch]);

  useEffect(() => {
    setHighlight(0);
  }, [query]);

  // Keep the active option visible while arrow-navigating. Cheap lookup — the
  // popover is short, and `nearest` avoids the page jumping when it's in view.
  useEffect(() => {
    if (!open || !showSearch || !listRef.current) return;
    const el = listRef.current.querySelector(`#${CSS.escape(optionId(highlight))}`);
    el?.scrollIntoView({ block: 'nearest' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [highlight, open, showSearch]);

  const selected = options.find((o) => getKey(o) === value);

  const commit = (o) => {
    onChange(getKey(o));
    setOpen(false);
  };

  const onKeyDown = (e) => {
    // Typing in the field must not leak to the page — e.g. the Log Time form
    // listens for Escape on `document` to cancel, and App-level letter
    // shortcuts could otherwise fire. Stop the synthetic event here so it
    // never reaches those native document/window handlers.
    e.stopPropagation();
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => Math.min(filtered.length - 1, h + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(0, h - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      // Sole-match shortcut: a single result + Enter picks it regardless of
      // where the highlight sits.
      const pick = filtered.length === 1 ? filtered[0] : filtered[highlight];
      if (pick) commit(pick);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
    }
  };

  const renderOption = (o, i) => {
    const desc = getDescription && getDescription(o);
    const isSelected = getKey(o) === value;
    const isHighlighted = showSearch && i === highlight;
    return (
      <div
        key={getKey(o)}
        id={showSearch ? optionId(i) : undefined}
        role={showSearch ? 'option' : undefined}
        aria-selected={showSearch ? isSelected : undefined}
        className={
          'tt-pop-item ' +
          (isSelected ? 'is-active' : '') +
          (desc ? ' has-desc' : '') +
          (isHighlighted ? ' is-active' : '')
        }
        onMouseEnter={showSearch ? () => setHighlight(i) : undefined}
        onClick={() => commit(o)}
        title={desc || undefined}
      >
        {desc ? (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              flex: 1,
              minWidth: 0,
            }}
          >
            <span style={{ fontWeight: 500, color: 'var(--sp-fg-1)' }}>
              {getLabel(o)}
            </span>
            <span
              style={{
                fontSize: 12,
                color: 'var(--sp-fg-3)',
                lineHeight: 1.4,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {desc}
            </span>
          </div>
        ) : (
          <span>{getLabel(o)}</span>
        )}
        {getMeta && <span className="tt-pop-meta">{getMeta(o)}</span>}
        {isSelected && (
          <span style={{ marginLeft: 'auto', color: 'var(--sp-fg-1)' }}>
            <Icon name="check" size={14} />
          </span>
        )}
      </div>
    );
  };

  const optionEls = filtered.map(renderOption);

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        type="button"
        className={'tt-input ' + (error ? 'is-error' : '')}
        onClick={() => !disabled && setOpen((o) => !o)}
        disabled={disabled}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          textAlign: 'left',
          cursor: 'pointer',
        }}
      >
        <span
          style={{
            color: selected ? 'var(--sp-fg-1)' : 'var(--sp-fg-3)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {selected ? getLabel(selected) : placeholder}
        </span>
        <span
          style={{
            color: 'var(--sp-fg-3)',
            display: 'inline-flex',
            marginLeft: 8,
          }}
        >
          <Icon name="chevD" size={16} />
        </span>
      </button>
      {open && (
        <div className="tt-pop" style={{ top: 'calc(100% + 4px)', left: 0 }}>
          {showSearch && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '6px 10px',
                borderBottom: '1px solid var(--sp-border, #E9E9E9)',
              }}
            >
              <span style={{ color: 'var(--sp-fg-3)', display: 'inline-flex' }}>
                <Icon name="search" size={14} />
              </span>
              <input
                ref={inputRef}
                type="text"
                role="combobox"
                aria-expanded="true"
                aria-controls={listboxId}
                aria-autocomplete="list"
                aria-activedescendant={
                  filtered[highlight] ? optionId(highlight) : undefined
                }
                aria-label={searchPlaceholder}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={onKeyDown}
                placeholder={searchPlaceholder}
                style={{
                  flex: 1,
                  border: 0,
                  outline: 0,
                  background: 'transparent',
                  fontSize: 13,
                  color: 'var(--sp-fg-1)',
                  padding: '2px 0',
                }}
              />
            </div>
          )}
          {/* Clear / empty row — only while the search is empty, so it doesn't
              muddle the filtered results (same rule as ClientSelect). */}
          {emptyLabel && !query.trim() && (
            <div
              className="tt-pop-item"
              onClick={() => {
                onChange(null);
                setOpen(false);
              }}
            >
              <span style={{ color: 'var(--sp-fg-3)', fontStyle: 'italic' }}>
                {emptyLabel}
              </span>
            </div>
          )}
          {showSearch && query.trim() && filtered.length === 0 && (
            // Muted, italic dead-end. No create/add affordance — the options
            // are a fixed dictionary. `aria-live` announces it to SR users.
            <div
              className="tt-pop-item"
              role="status"
              aria-live="polite"
              style={{
                color: 'var(--sp-fg-3)',
                fontStyle: 'italic',
                cursor: 'default',
              }}
            >
              {searchNoun
                ? `No ${searchNoun} matches “${query.trim()}”.`
                : `No matches for “${query.trim()}”.`}
            </div>
          )}
          {showSearch ? (
            <div ref={listRef} id={listboxId} role="listbox">
              {optionEls}
            </div>
          ) : (
            optionEls
          )}
        </div>
      )}
    </div>
  );
}
